import axios from 'axios';
import { toast } from 'sonner';
import { firstCutoverBlock } from './firstCutoverPolicy';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
export const API_BASE = `${BACKEND_URL}/api`;
export const IS_READ_ONLY_STAGING = process.env.REACT_APP_READ_ONLY_MODE === 'true';
const DEPLOYMENT_ENVIRONMENT = (process.env.REACT_APP_ENVIRONMENT || '').toLowerCase();
const AUTH_MODE = (process.env.REACT_APP_AUTH_MODE || 'legacy').toLowerCase();

const READ_ONLY_METHODS = new Set(['get', 'head', 'options']);
const SAFE_STAGING_POST_PATHS = new Set([
  '/auth/login',
  '/auth/logout',
  '/cart/quote',
]);
if (AUTH_MODE === 'firebase') {
  SAFE_STAGING_POST_PATHS.add('/auth/firebase-login-identifier');
  SAFE_STAGING_POST_PATHS.add('/auth/firebase-session');
}

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const method = (config.method || 'get').toLowerCase();
  if (method === 'get' && !config.timeout) config.timeout = 15000;
  const path = String(config.url || '').split('?')[0];
  const stagingSafePost = method === 'post' && (SAFE_STAGING_POST_PATHS.has(path) || /^\/admin\/orders\/[^/]+\/cost-invoice\/export$/.test(path));

  if (IS_READ_ONLY_STAGING && !READ_ONLY_METHODS.has(method) && !stagingSafePost) {
    const error = new Error(
      DEPLOYMENT_ENVIRONMENT === 'staging'
        ? 'This Firebase staging preview is read-only. No production data was changed.'
        : 'The site is temporarily read-only. No data was changed.'
    );
    error.code = 'ERR_STAGING_READ_ONLY';
    try { toast.error(error.message); } catch { /* ignore toast errors */ }
    return Promise.reject(error);
  }

  const cutoverBlock = firstCutoverBlock(method, path);
  if (cutoverBlock) {
    const error = new Error(`${cutoverBlock.reason} No request was sent.`);
    error.code = 'ERR_FIRST_CUTOVER_DISABLED';
    error.route = `${cutoverBlock.method} ${cutoverBlock.path}`;
    try { toast.error(error.message); } catch { /* ignore toast errors */ }
    return Promise.reject(error);
  }

  return config;
});

// Endpoints that legitimately may return 401/403 without meaning "session is dead"
// (e.g. login attempts with wrong creds, password-reset with invalid token, etc.)
// We DO NOT auto-logout on 401 for these.
const NON_SESSION_AUTH_PATHS = [
  '/auth/login',
  '/auth/firebase-session',
  '/auth/firebase-login-identifier',
  '/auth/register',
  '/auth/upgrade-guest',
  '/auth/forgot-password',
  '/auth/reset-password',
  '/integration/',           // bot/CS API uses its own X-API-Key, not user token
];

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const url = error.config?.url || '';
    const detail = error.response?.data?.detail || '';

    if (status === 401) {
      const isSilentSessionProbe = url.includes('/auth/me');
      const isExempt = isSilentSessionProbe || NON_SESSION_AUTH_PATHS.some((p) => url.includes(p));
      if (!isExempt) {
        // Session-related 401 (Invalid token / Token expired / Not authenticated / User not found).
        // Clear stale auth, but preserve the cart so the user can resume after re-login.
        const stalePath = window.location.pathname + window.location.search + window.location.hash;
        const preserveReturn = !['/login', '/register', '/forgot-password', '/reset-password'].includes(window.location.pathname);

        try { localStorage.removeItem('token'); } catch {}
        try { localStorage.removeItem('user'); } catch {}

        // Friendly user-facing toast
        try {
          toast.error(
            typeof detail === 'string' && detail.toLowerCase().includes('expired')
              ? 'Your session expired. Please sign in again to continue.'
              : 'Please sign in again to continue.',
            { duration: 4000 }
          );
        } catch {}

        if (preserveReturn) {
          try { sessionStorage.setItem('returnTo', stalePath); } catch {}
        }

        // Small delay so the toast renders before redirect
        setTimeout(() => {
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }, 600);
      }
    }
    return Promise.reject(error);
  }
);

export default api;
